import { PenIcon, TrashIcon } from "@xola/icons";
import { Button, flash, numberFormat, Table, Tooltip } from "@xola/ui-kit";
import { compact, isNil } from "lodash";
import pluralize from "pluralize";
import React, { ComponentType, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Affiliate,
    CouponAlgorithm,
    LineItemTemplate,
    LineItemType,
    Partner,
    PartnerObject,
    Permission,
    PurchaseRule,
    PurchaseRuleLink,
    TagId,
    TermsType,
    XolaPartner,
    XolaPartnerStatus,
} from "types";
import { InfiniteList } from "../../Common/InfiniteList";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { TableSkeleton } from "../../Common/Skeleton/TableSkeleton";
import { api } from "@/api/api";
import { PromptModal } from "@/components/Common/PromptModal";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getPurchaseRulesByTag } from "@/models/PurchaseRule";
import {
    getAlgorithm,
    getAmount,
    getDemographicSelection,
    getIncludesAddOns,
    getProductSelection,
    getTermsType,
} from "@/models/TermsTemplatePurchaseRule";
import { useLocalizationStore } from "@/stores/localizationStore";

interface TermsTemplateTableProps {
    showActions: boolean;
    selectedTermsTemplate?: PurchaseRuleLink;

    // Swap-able components
    components?: {
        Name: ComponentType<TermsTemplateNameProps>;
    };
}

export const TermsTemplateTable = ({
    showActions = false,
    selectedTermsTemplate,
    components,
}: TermsTemplateTableProps) => {
    const { Name } = components ?? { Name: TermsTemplateName };
    const history = useHistory();
    const seller = useSeller();
    const locale = useLocalizationStore().getLocale();
    const invalidate = useInvalidate();
    const [templateToDelete, setTemplateToDelete] = useState<PurchaseRuleLink>();

    const {
        data: termsTemplates,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
        hasNextPage,
    } = useInfiniteCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": "Affiliate",
    });

    const filteredTermsTemplates = useMemo(() => {
        return termsTemplates.filter((localTermTemplate) => localTermTemplate.id !== selectedTermsTemplate?.id);
    }, [termsTemplates, selectedTermsTemplate]);

    const { data: purchaseRules, isLoading: isLoadingPurchaseRules } = useAutoFetchCollection<PurchaseRule>(
        "purchaseRules",
        {
            "tags._id": "Affiliate",
        },
    );

    const { data: partners, isLoading: isLoadingPartners } = useAutoFetchCollection<Partner>(["partners"], {
        seller: seller.id,
    });

    const xolaPartners = useMemo(
        () => partners.filter((partner) => partner.object === PartnerObject.XOLA_PARTNER) as XolaPartner[],
        [partners],
    );

    const agents = useMemo(
        () => partners.filter((partner) => partner.object === PartnerObject.AFFILIATE) as Affiliate[],
        [partners],
    );

    const { data: demographicTemplates } = useAutoFetchCollection<LineItemTemplate>(["lineItemTemplates"], {
        type: LineItemType.DEMOGRAPHIC,
    });

    const { mutateAsync: deleteTermsTemplate } = useMutation(async () => {
        if (templateToDelete?.id) {
            await api.delete(`/purchaseRuleLinks/${templateToDelete.id}`);
            setTemplateToDelete(undefined);
            flash.success("Terms template deleted");
            invalidate("purchaseRuleLinks");
        }
    });

    if (isLoading || isLoadingPurchaseRules || isLoadingPartners) {
        return <TableSkeleton rows={7} columns={5} />;
    }

    return (
        <>
            {termsTemplates?.length > 0 ? (
                <InfiniteList isLoading={isFetchingNextPage} hasMore={hasNextPage} onLoadMore={fetchNextPage}>
                    <Table>
                        <Table.Head>
                            <Table.Row>
                                <Table.Header>Template</Table.Header>
                                <Table.Header>Products</Table.Header>
                                <Table.Header>Applies to</Table.Header>
                                <Table.Header>Discount</Table.Header>
                                <Table.Header>Commission</Table.Header>

                                {showActions ? (
                                    <IsGranted permissions={Permission.MODIFY_PARTNERS}>
                                        <Table.Header className="text-left">Action</Table.Header>
                                    </IsGranted>
                                ) : null}
                            </Table.Row>
                        </Table.Head>

                        <Table.Body>
                            {filteredTermsTemplates.map((termsTemplate) => (
                                <Table.Row key={termsTemplate.id} className="border-b border-gray-lighter">
                                    <Table.Cell className="no-translate !whitespace-normal">
                                        <Name termsTemplate={termsTemplate} />
                                    </Table.Cell>

                                    <Table.Cell data-testid="terms-template-table-products">
                                        <ProductCount termsTemplate={termsTemplate} purchaseRules={purchaseRules} />
                                    </Table.Cell>

                                    <Table.Cell data-testid="terms-template-table-agents">
                                        <PartnerList
                                            termsTemplate={termsTemplate}
                                            xolaPartners={xolaPartners}
                                            agents={agents}
                                        />
                                    </Table.Cell>

                                    <Table.Cell data-testid="terms-template-table-discount">
                                        {getSummary(
                                            seller.currency,
                                            locale,
                                            LineItemType.AFFILIATE_DISCOUNT,
                                            termsTemplate,
                                            purchaseRules,
                                            demographicTemplates,
                                        )}
                                    </Table.Cell>

                                    <Table.Cell data-testid="terms-template-table-commission">
                                        {getSummary(
                                            seller.currency,
                                            locale,
                                            LineItemType.AFFILIATE_COMMISSION,
                                            termsTemplate,
                                            purchaseRules,
                                            demographicTemplates,
                                        )}
                                    </Table.Cell>

                                    {showActions ? (
                                        <IsGranted permissions={Permission.MODIFY_TERMS_TEMPLATES}>
                                            <Table.Cell className="space-x-1 text-left">
                                                <IsGranted permissions={Permission.MODIFY_TERMS_TEMPLATES}>
                                                    <Button
                                                        variant="outline"
                                                        color="secondary"
                                                        size="tiny"
                                                        data-testid="terms-template-item-edit-button"
                                                        onClick={() => {
                                                            history.push(
                                                                `/distribution/termsTemplates/${termsTemplate.id}`,
                                                            );
                                                        }}
                                                    >
                                                        <PenIcon />
                                                    </Button>
                                                    {isTermsTemplateUnlinked(termsTemplate, xolaPartners, agents) && (
                                                        <span>
                                                            <Button
                                                                variant="outline"
                                                                color="secondary"
                                                                size="tiny"
                                                                data-testid="terms-template-item-delete-button"
                                                                onClick={() => setTemplateToDelete(termsTemplate)}
                                                            >
                                                                <TrashIcon />
                                                            </Button>
                                                        </span>
                                                    )}
                                                </IsGranted>
                                            </Table.Cell>
                                        </IsGranted>
                                    ) : null}
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </InfiniteList>
            ) : (
                <span className="flex items-center justify-center pt-25" data-testid="agent-list-no-agents-label">
                    No Terms Template found. Click the button above to create a new terms template
                </span>
            )}

            <IsGranted permissions={Permission.MODIFY_TERMS_TEMPLATES}>
                <PromptModal
                    isOpen={!!templateToDelete}
                    title="Delete Terms Template"
                    onConfirm={() => {
                        deleteTermsTemplate();
                    }}
                    onClose={() => setTemplateToDelete(undefined)}
                >
                    <div>Are you sure you want to delete this terms template?</div>
                </PromptModal>
            </IsGranted>
        </>
    );
};

interface TermsTemplateNameProps {
    termsTemplate: PurchaseRuleLink;
}

const TermsTemplateName = ({ termsTemplate }: TermsTemplateNameProps) => {
    return <span>{termsTemplate.name ?? "-"}</span>;
};

const ProductCount = ({
    termsTemplate,
    purchaseRules,
}: {
    termsTemplate: PurchaseRuleLink;
    purchaseRules: PurchaseRule[];
}) => {
    const purchaseRule = purchaseRules.find((purchaseRule) => {
        return termsTemplate.purchaseRules.some((termsTemplatePurchaseRule) => {
            return (
                termsTemplatePurchaseRule.purchaseRulePermanentId === purchaseRule.permanentId ||
                termsTemplatePurchaseRule.purchaseRulePermanentId === purchaseRule.id
            );
        });
    });
    const allProductsElement = <span>All products</span>;

    if (purchaseRule) {
        const productSelected = {
            all: true,
            selected: 0,
        };

        const termsType = getTermsType(termsTemplate);

        if (termsType === TermsType.ADVANCED) {
            const partnerDemographicTermsIds = new Set(
                termsTemplate.purchaseRules.map((purchaseRules) => purchaseRules.purchaseRulePermanentId),
            );
            const discountPurchaseRules = purchaseRules.filter((purchaseRule) => {
                return (
                    partnerDemographicTermsIds.has(purchaseRule.id) &&
                    purchaseRule?.tags?.find((tag) => tag.id === LineItemType.AFFILIATE_DISCOUNT)
                );
            });
            productSelected.all = discountPurchaseRules.length === 0;
            productSelected.selected = discountPurchaseRules.length;
        } else {
            const selection = getProductSelection(purchaseRule);
            productSelected.all = selection.all;
            productSelected.selected = selection.selected.length;
        }

        return productSelected.all ? (
            allProductsElement
        ) : (
            <span>
                <var data-var="productCount">{productSelected.selected}</var>{" "}
                {pluralize("product", productSelected.selected)}
            </span>
        );
    }

    return allProductsElement;
};

const PartnerListTooltip = ({ agents = [], operators = [] }: { agents?: Affiliate[]; operators?: XolaPartner[] }) => {
    if (agents.length === 0 && operators.length === 0) {
        return null;
    }

    return (
        <span className="max-h-60 w-40 space-y-2 overflow-y-auto">
            <div className="space-y-1">
                <span>
                    {agents.length > 0 ? (
                        <span className="font-bold">
                            <var data-var="agentCount">{agents.length}</var> Agents
                        </span>
                    ) : null}
                </span>
                <div>
                    {agents?.map((agent) => (
                        <NoTranslate key={agent.id} as="div">
                            {agent.name}
                        </NoTranslate>
                    ))}
                </div>
            </div>
            <div className="space-y-1">
                <span>
                    {operators?.length > 0 ? (
                        <span className="font-bold">
                            <var data-var="operatorCount">{operators.length}</var> Operators
                        </span>
                    ) : null}
                </span>
                <div>
                    {operators.map((operator) => (
                        <NoTranslate key={operator.id} as="div">
                            {operator.partnerSeller?.name}
                        </NoTranslate>
                    ))}
                </div>
            </div>
        </span>
    );
};

const getLinkedOperators = (termsTemplate: PurchaseRuleLink, xolaPartners: XolaPartner[]) => {
    const linkedOperators = xolaPartners.filter(
        (parnter) => parnter.status === XolaPartnerStatus.ACCEPTED && parnter.purchaseRuleLink?.id === termsTemplate.id,
    );

    return linkedOperators;
};

const getLinkedAgents = (termsTemplate: PurchaseRuleLink, agents: Affiliate[]) => {
    const linkedAgents = compact(
        termsTemplate.entities.map((entity) => agents.find((agent) => agent.id === entity.id)),
    );

    return linkedAgents;
};

const PartnerList = ({
    termsTemplate,
    xolaPartners,
    agents,
}: {
    termsTemplate: PurchaseRuleLink;
    xolaPartners: XolaPartner[];
    agents: Affiliate[];
}) => {
    const linkedAgents = getLinkedAgents(termsTemplate, agents);
    const linkedOperators = getLinkedOperators(termsTemplate, xolaPartners);

    const totalPartners = linkedOperators.length + linkedAgents.length;

    if (totalPartners === 0) {
        return <span>No partners</span>;
    }

    return (
        <Tooltip content={<PartnerListTooltip agents={linkedAgents} operators={linkedOperators} />}>
            <span className="cursor-pointer border-b">
                <var data-var="partnerCount">{totalPartners > 0 ? totalPartners : "No"}</var>{" "}
                {pluralize("partner", totalPartners)}
            </span>
        </Tooltip>
    );
};

const isTermsTemplateUnlinked = (termsTemplate: PurchaseRuleLink, xolaPartners: XolaPartner[], agents: Affiliate[]) => {
    const linkedAgents = getLinkedAgents(termsTemplate, agents);
    const linkedOperators = getLinkedOperators(termsTemplate, xolaPartners);

    const totalPartners = linkedOperators.length + linkedAgents.length;

    return totalPartners === 0;
};

const getSummary = (
    currency: string,
    locale: string,
    lineItemType: LineItemType,
    termsTemplate: PurchaseRuleLink,
    purchaseRules: PurchaseRule[],
    demographicTemplates: LineItemTemplate[],
) => {
    const termsType = getTermsType(termsTemplate);

    if (termsType === TermsType.ADVANCED) {
        return "Varies by demographic";
    }

    const purchaseRuleIds = termsTemplate?.purchaseRules.map((purchaseRule) => {
        return purchaseRule.purchaseRulePermanentId;
    });

    const templatePurchaseRules = purchaseRules.filter((purchaseRule) => purchaseRuleIds.includes(purchaseRule.id));
    const exceptionPurchaseRules = getPurchaseRulesByTag(TagId.AFFILIATE_OVERRIDE, templatePurchaseRules);

    if (exceptionPurchaseRules.length > 0) {
        return "Varies by product";
    }

    const purchaseRule = templatePurchaseRules.find((purchaseRule) => {
        return (
            purchaseRule.tags?.some((tag) => tag.id === lineItemType) ??
            purchaseRule.name?.startsWith(lineItemType === LineItemType.AFFILIATE_DISCOUNT ? "Discount" : "Commission")
        );
    });

    if (!purchaseRule) {
        if (lineItemType === LineItemType.AFFILIATE_DISCOUNT) {
            return "--";
        }

        return (
            <span>
                <var data-var="commissionPercentage">0</var>% Commission on all demographics with add-ons`
            </span>
        );
    }

    const demographicSelection = getDemographicSelection(purchaseRule, demographicTemplates);
    const amount = Math.abs(getAmount(purchaseRule));

    if ((isNil(amount) || !amount) && lineItemType === LineItemType.AFFILIATE_DISCOUNT) {
        return "--";
    }

    const includeAddons = getIncludesAddOns(purchaseRule);

    const demographicSelectionTotal = demographicTemplates.filter((template) =>
        demographicSelection.selected.includes(template.id),
    ).length;

    const summary = [
        lineItemType === LineItemType.AFFILIATE_DISCOUNT ? "off" : "on",
        demographicSelection.all ? "all" : demographicSelectionTotal,
        "demographics",
        includeAddons ? "with" : "without",
        "add-ons",
    ].join(" ");

    return (
        <span>
            <var data-var="value">
                {getAlgorithm(purchaseRule) === CouponAlgorithm.ABSOLUTE
                    ? numberFormat(amount, currency, locale)
                    : `${amount}%`}
            </var>{" "}
            {summary}
        </span>
    );
};
